import {create} from 'zustand';
import {AgentType, AgentTypes} from '../../../shared/agent';
import {useAgentBuilderStore} from './AgentBuilder.state';
import {useCallback, useEffect} from 'react';
import _ from 'lodash';
import {UseMutationResult} from '@tanstack/react-query';
import {getAnalystFieldTypeFromStore} from './AiFieldDefinitionEditor.state';
import {useAgentAnalystField} from './helpers';

const Steps = [
  {
    step: 'setName',
    agentTypes: 'all',
  },
  {
    step: 'selectAccounts',
    agentTypes: 'all',
  },
  {
    step: 'editResearchField',
    agentTypes: ['research', 'researchAndAnalyst'],
  },
  {
    step: 'editLegacyResearchField',
    agentTypes: ['legacyResearch', 'legacyResearchAndAnalyst'],
  },
  {
    step: 'editTeamSizeField',
    agentTypes: ['teamSize'],
  },
  {
    step: 'editLegacyTeamSizeField',
    agentTypes: ['legacyTeamSize'],
  },
  {
    step: 'editAnalystField',
    agentTypes: ['analyst', 'legacyResearchAndAnalyst', 'researchAndAnalyst'],
  },
  {
    step: 'analyzeLists',
    agentTypes: ['analyst', 'legacyResearchAndAnalyst', 'researchAndAnalyst'],
  },
  {
    step: 'reviewSettings',
    agentTypes: 'all',
  },
] as const satisfies {
  step: string;
  agentTypes: AgentType[] | 'all';
}[];

export type AgentBuilderStep = (typeof Steps)[number]['step'];

const AgentBuilderSteps = AgentTypes.reduce(
  (acc, agentType) => {
    const steps = Steps.filter(
      ({agentTypes}) =>
        agentTypes === 'all' || _.includes(agentTypes, agentType)
    ).map(({step}) => step);
    return {
      ...acc,
      [agentType]: steps,
    };
  },
  {} as Record<AgentType, AgentBuilderStep[]>
);

interface AgentBuilderNavState {
  step: AgentBuilderStep;
  steps: readonly AgentBuilderStep[];

  setStep: (step: AgentBuilderStep) => void;
  setSteps: (steps: readonly AgentBuilderStep[]) => void;

  nextStep: () => void;
  prevStep: () => void;
  navigate: (direction: 'next' | 'prev') => void;
}

const useAgentBuilderNavState = create<AgentBuilderNavState>()((set, get) => {
  const getCurrentStepIdx = () => get().steps.indexOf(get().step);

  return {
    step: 'setName',
    steps: [],

    setStep: (step) => set({step}),
    setSteps: (steps) => set({steps}),

    nextStep: () => get().navigate('next'),
    prevStep: () => get().navigate('prev'),
    navigate: (direction) => {
      const newStep =
        get().steps[getCurrentStepIdx() + (direction === 'next' ? 1 : -1)];
      if (newStep) {
        set({step: newStep});
      }
    },
  };
});

export const useAgentBuilderNavStore = () => {
  const {serverState, type} = useAgentBuilderStore();
  const agentType = type ?? serverState?.type ?? 'analyst';
  const store = useAgentBuilderNavState();
  const {setSteps} = store;

  const editorFieldType = getAnalystFieldTypeFromStore(agentType);
  const serverFieldType = useAgentAnalystField(serverState?.id)?.config
    .fieldType;

  // Prefer draft (unsaved) field type, fall back to server state
  const analystFieldType = editorFieldType ?? serverFieldType;

  // Skip analyzeLists for estimate fields (no discrete values to show)
  useEffect(() => {
    const baseSteps = AgentBuilderSteps[agentType];
    setSteps(
      analystFieldType === 'estimate'
        ? baseSteps.filter((s) => s !== 'analyzeLists')
        : baseSteps
    );
  }, [agentType, analystFieldType, setSteps]);

  return store;
};

export const useAgentNavigation = ({
  mutation,
  saveOnBack,
}: {
  mutation: UseMutationResult<unknown, Error, void, unknown>;
  saveOnBack: boolean;
}) => {
  const {navigate} = useAgentBuilderNavStore();

  return useCallback(
    (direction: 'next' | 'prev') => {
      if (direction === 'prev' && !saveOnBack) {
        navigate(direction);
        return;
      }

      mutation.mutate(undefined, {
        onSuccess: () => navigate(direction),
      });
    },
    [navigate, mutation, saveOnBack]
  );
};
